import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import I3PageHero from '../components/I3PageHero';
import I3RichHtml from '../components/I3RichHtml';
import I3ServiceDetailDialog from '../components/I3ServiceDetailDialog';
import { useRevealSections } from '../hooks/useReveal';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import type { I3ServiceCard } from '../cms/types';
import '../styles/I3Page.css';
import '../styles/I3Reportage.css';
import '../styles/I3Services.css';

const CONTACT_PATH = '/contact';

/** External scheduling (Calendly, etc.); speaking inquiries always go to Contact */
function cardUsesExternalBookUrl(card: I3ServiceCard): boolean {
  if (card.id === 'speaking-engagements') return false;
  return Boolean(card.bookUrl?.trim());
}

const I3Services: React.FC = () => {
  const { content, loading, error, ready } = useI3SiteContent();
  const { services } = content;
  const wrapRef = useRef<HTMLDivElement>(null);
  useRevealSections(wrapRef);

  const [openCard, setOpenCard] = useState<I3ServiceCard | null>(null);

  const heroSubtitle =
    services.pageSubtitle ??
    'Marriage enrichment, leadership development, financial wellness';

  return (
    <article className="i3-page i3-reportage-page i3-services-page">
      <I3PageHero showLogo title="Services" subtitle={heroSubtitle} />
      <div className="i3-page-wrap" ref={wrapRef}>
        {loading && !ready && <p className="i3-cms-loading">Loading services from CMS…</p>}
        {error && ready && (
          <p className="i3-cms-error" role="status">
            CMS fetch had an issue; showing cached or default content. {error.message}
          </p>
        )}

        {services.pillars.map((pillar) => (
          <section
            key={pillar.id}
            className={`i3-section i3-services-pillar i3-reveal${
              pillar.hidePillarHeading ? ' i3-services-pillar--no-heading' : ''
            }`}
            aria-labelledby={pillar.hidePillarHeading ? undefined : `i3-pillar-${pillar.id}`}
            aria-label={pillar.hidePillarHeading ? pillar.title : undefined}
          >
            {!pillar.hidePillarHeading ? (
              <header className="i3-services-pillar-head">
                <h2 id={`i3-pillar-${pillar.id}`} className="i3-services-pillar-title">
                  {pillar.title}
                </h2>
                {pillar.subtitle ? (
                  <p className="i3-services-pillar-sub">{pillar.subtitle}</p>
                ) : null}
              </header>
            ) : null}
            <div className="i3-services-card-grid">
              {pillar.cards.map((card) => (
                <article key={card.id} className="i3-services-card" data-service-card={card.id}>
                  <p className="i3-services-card-for">{card.whoItsFor}</p>
                  <h3 className="i3-services-card-title">{card.title}</h3>
                  <I3RichHtml className="i3-services-card-summary" html={card.summary} />
                  <div className="i3-services-card-actions">
                    <button
                      type="button"
                      className="i3-cta i3-cta-secondary"
                      onClick={() => setOpenCard(card)}
                    >
                      Details
                    </button>
                    {cardUsesExternalBookUrl(card) ? (
                      <a
                        href={card.bookUrl}
                        className="i3-cta i3-cta-primary"
                      >
                        Book
                      </a>
                    ) : (
                      <Link to={CONTACT_PATH} className="i3-cta i3-cta-primary">
                        Book
                      </Link>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}

        <section className="i3-section i3-services-cta i3-anchor-dark i3-reveal" aria-label="Get in touch">
          <h2>Begin with a conversation</h2>
          <p>
            Not sure which offering fits? Reach out—we&apos;ll help you choose the right path and next
            step.
          </p>
          <div className="i3-services-cta-row">
            <Link to={CONTACT_PATH} className="i3-cta i3-cta-primary">
              Contact us
            </Link>
          </div>
        </section>
      </div>

      {openCard ? (
        <I3ServiceDetailDialog
          open
          onClose={() => setOpenCard(null)}
          title={openCard.title}
          detailHtml={openCard.detailHtml}
          bookUrl={cardUsesExternalBookUrl(openCard) ? openCard.bookUrl : ''}
        />
      ) : null}
    </article>
  );
};

export default I3Services;
